import * as THREE from 'three';

export const GRANITE_SURFACE_FINISH = Object.freeze({
    AUTO: 'auto',
    UNPOLISHED: 'unpolished',
    POLISHED: 'polished',
    UNKNOWN: 'unknown',
});

export const GRANITE_APPEARANCE = Object.freeze({
    unpolished: Object.freeze({
        roughness: 0.97,
        metalness: 0,
        clearcoat: 0,
        clearcoatRoughness: 1,
        envMapIntensity: 0.1,
        colorMultiplier: 0.72,
        specularIntensity: 0.18,
        ior: 1.46,
    }),
    polished: Object.freeze({
        roughness: 0.16,
        metalness: 0,
        clearcoat: 0.46,
        clearcoatRoughness: 0.1,
        envMapIntensity: 0.78,
        colorMultiplier: 0.96,
        specularIntensity: 0.72,
        ior: 1.5,
    }),
});

const toFinite = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, min, max, fallback) => (
    Math.min(max, Math.max(min, toFinite(value, fallback)))
);

const normalizeAppearancePreset = (preset) => (
    preset && typeof preset === 'object' && !Array.isArray(preset)
        ? preset
        : {}
);

const copyTextureReference = (target, source, key) => {
    if (!source?.[key]?.isTexture) return;
    target[key] = source[key];
};

/**
 * Переводит обычный MeshStandardMaterial в MeshPhysicalMaterial, сохраняя
 * карты и базовые параметры. Благодаря этому clearcoat действительно
 * работает и полированная поверхность получает заметный блик.
 */
export const ensureGranitePhysicalMaterial = (sourceMaterial) => {
    if (!sourceMaterial) return sourceMaterial;
    if (sourceMaterial.isMeshPhysicalMaterial) return sourceMaterial;

    const target = new THREE.MeshPhysicalMaterial();

    try {
        target.copy(sourceMaterial);
    } catch (_error) {
        if (sourceMaterial.color?.isColor) target.color.copy(sourceMaterial.color);
        target.opacity = toFinite(sourceMaterial.opacity, 1);
        target.transparent = sourceMaterial.transparent === true;
        target.side = sourceMaterial.side ?? THREE.DoubleSide;
    }

    [
        'map',
        'normalMap',
        'roughnessMap',
        'metalnessMap',
        'aoMap',
        'emissiveMap',
        'alphaMap',
        'bumpMap',
        'displacementMap',
        'lightMap',
        'envMap',
    ].forEach((key) => copyTextureReference(target, sourceMaterial, key));

    target.name = sourceMaterial.name || target.name;
    target.userData = { ...(sourceMaterial.userData || {}) };
    target.side = sourceMaterial.side ?? THREE.DoubleSide;
    target.roughness = toFinite(target.roughness, 0.55);
    target.metalness = toFinite(target.metalness, 0);
    target.clearcoat = toFinite(target.clearcoat, 0);
    target.clearcoatRoughness = toFinite(target.clearcoatRoughness, 1);
    target.specularIntensity = toFinite(target.specularIntensity, 1);
    target.ior = toFinite(target.ior, 1.5);
    target.envMapIntensity = toFinite(target.envMapIntensity, 1);
    target.vertexColors = sourceMaterial.vertexColors === true;
    target.skinning = sourceMaterial.skinning === true;
    target.morphTargets = sourceMaterial.morphTargets === true;
    target.morphNormals = sourceMaterial.morphNormals === true;
    target.needsUpdate = true;

    sourceMaterial.dispose?.();

    return target;
};

const getAppearance = (finish, preset) => {
    const safePreset = normalizeAppearancePreset(preset);

    if (finish === GRANITE_SURFACE_FINISH.UNPOLISHED) {
        return {
            ...GRANITE_APPEARANCE.unpolished,
            roughness: clamp(
                safePreset.unpolishedRoughness,
                0.72,
                1,
                GRANITE_APPEARANCE.unpolished.roughness
            ),
            colorMultiplier: clamp(
                safePreset.unpolishedColorMultiplier,
                0.55,
                1,
                GRANITE_APPEARANCE.unpolished.colorMultiplier
            ),
        };
    }

    if (finish === GRANITE_SURFACE_FINISH.POLISHED) {
        return {
            ...GRANITE_APPEARANCE.polished,
            roughness: clamp(
                safePreset.polishedRoughness,
                0.02,
                0.35,
                GRANITE_APPEARANCE.polished.roughness
            ),
            clearcoat: clamp(
                safePreset.clearcoat,
                0,
                1,
                GRANITE_APPEARANCE.polished.clearcoat
            ),
            clearcoatRoughness: clamp(
                safePreset.clearcoatRoughness,
                0,
                0.4,
                GRANITE_APPEARANCE.polished.clearcoatRoughness
            ),
        };
    }

    return {
        roughness: clamp(safePreset.roughness, 0, 1, 0.55),
        metalness: clamp(safePreset.metalness, 0, 1, 0),
        clearcoat: 0,
        clearcoatRoughness: 1,
        envMapIntensity: 0.65,
        colorMultiplier: 1,
        specularIntensity: 0.7,
        ior: 1.5,
    };
};

/**
 * Применяет только внешний вид поверхности. Текстуру выбирает вызывающий
 * код, поэтому модуль одинаково подходит админке и основному frontend.
 *
 * preset может быть null, если для выбранной поверхности ещё нет
 * настроенного texturePreset. В этом случае применяются безопасные
 * значения GRANITE_APPEARANCE вместо падения 3D-сцены.
 */
export const applyGraniteSurfaceAppearance = (
    material,
    finish,
    preset = {}
) => {
    if (!material) return material;

    const appearance = getAppearance(finish, preset);

    if (typeof material.roughness === 'number') material.roughness = appearance.roughness;
    if (typeof material.metalness === 'number') material.metalness = appearance.metalness;
    if (typeof material.clearcoat === 'number') material.clearcoat = appearance.clearcoat;
    if (typeof material.clearcoatRoughness === 'number') {
        material.clearcoatRoughness = appearance.clearcoatRoughness;
    }
    if (typeof material.envMapIntensity === 'number') {
        material.envMapIntensity = appearance.envMapIntensity;
    }
    if (typeof material.specularIntensity === 'number') {
        material.specularIntensity = appearance.specularIntensity;
    }
    if (typeof material.ior === 'number') material.ior = appearance.ior;

    if (material.specularColor?.isColor) material.specularColor.setHex(0xffffff);
    if (material.emissive?.isColor) material.emissive.setHex(0x000000);
    if (typeof material.emissiveIntensity === 'number') material.emissiveIntensity = 0;
    if (material.color?.isColor) {
        const multiplier = appearance.colorMultiplier;
        material.color.setRGB(multiplier, multiplier, multiplier);
    }

    material.transparent = false;
    material.opacity = 1;
    material.depthWrite = true;
    material.needsUpdate = true;

    return material;
};
